import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Flex } from '@backstage/ui';
import { Combobox } from '@ns/plugin-ns-theme/combobox';
import type {
  GenericPickerConfig,
  OptionSource,
} from '@ns/plugin-picker-toolbox-contracts';
import {
  detectCapabilities,
  isCreatableOptionSource,
  isEnrichableOptionSource,
  isPaginatedOptionSource,
} from '@ns/plugin-picker-toolbox-contracts';
import type { AsyncResolverState } from '@ns/plugin-react-async-utils';
import { useAsyncResolver } from '@ns/plugin-react-async-utils';

import { resolveCascadeState } from './cascade';
import type { ResolvedOption } from './projection';
import {
  optionValueId,
  optionValueName,
  toResolvedOptions,
} from './projection';
import { decideSelection } from './selection';

/**
 * The context the picker feeds the async lifecycle primitive. Every input that
 * affects the resolve result is folded in (the cascade dependencies, the search
 * query) so the primitive's change-detection re-fetches exactly when it should.
 * `serverFiltered` toggles whether the query is part of the fetch context (server
 * side) or only a client-side filter that must NOT re-fetch. `paginated` toggles
 * whether the AsyncOp walks `resolvePage`+`nextCursor` or the single `resolve`.
 * `queryRequired` toggles whether a blank query keeps the source not-ready (see
 * `isReady` below): the source cannot resolve without a non-empty query.
 */
type ResolveContext = {
  dependencies: Record<string, unknown>;
  paginated: boolean;
  query: string;
  queryRequired: boolean;
  serverFiltered: boolean;
};

/**
 * The props of the generic picker. Datasource- and adapter-agnostic: it takes an
 * `OptionSource` (from a local mock or a remote transport, the picker does not
 * know which) and a `GenericPickerConfig`. The valid-key set (`validKeys`) and
 * the cross-step `formData` are injected by the Field layer from
 * `formContext.formData`; the picker never reads the RJSF subschema.
 *
 * The output contract is the FULL value object, not the id: `value` and
 * `onChange` are typed on `TValue` (the whole `getOptionValue(item)` object,
 * default `{ id, name }`), matching the NS Cloud template convention where a
 * template consumes `${{ parameters.field.name }}`. The id is used only as the
 * widget's selection key, never as the form value.
 */
export type ChoiceFieldProps<TItem, TValue, TConfig> = {
  /** The wrapper config: cascade refs + adapter-specific config. */
  config: GenericPickerConfig<TConfig>;
  /** The current cross-step form data (from `formContext.formData`). */
  formData: Record<string, unknown>;
  /** The field label shown to the user. */
  label?: string;
  /**
   * Called with the newly selected option's FULL value object (the whole
   * `getOptionValue(item)`, default `{ id, name }`), or `undefined` when cleared.
   */
  onChange: (value: TValue | undefined) => void;
  /**
   * Whether the dropdown opens as soon as the field gains focus (react-aria
   * `menuTrigger`). Defaults to `true` so a click always gives feedback. Set to
   * `false` for a source that should only open the menu once the user types.
   */
  openOnFocus?: boolean;
  /** Whether the field is required. */
  required?: boolean;
  /** The adapter that projects and resolves options. */
  source: OptionSource<TItem, TValue>;
  /**
   * The valid form-field keys (the owner of ref-validation). Injected by the
   * Field layer from `Object.keys(formContext.formData)`, optionally unioned
   * with the template parameter keys. A `dependsOn` ref outside this set is a
   * template bug and surfaces as an error, not a silent empty picker.
   */
  validKeys: ReadonlySet<string>;
  /** The currently selected option's value object, or undefined when unset. */
  value?: TValue;
};

/**
 * Fully drains a paginated source, following `nextCursor` until it is absent.
 * Exported so the page-merge (BLOCKER 2) is testable directly, without a DOM.
 */
export async function resolveAllPages<TItem, TValue>(
  source: OptionSource<TItem, TValue> & {
    resolvePage(input: {
      cursor?: string;
      dependencies: Record<string, unknown>;
      query?: string;
      signal: AbortSignal;
    }): Promise<{ items: TItem[]; nextCursor?: string }>;
  },
  input: {
    dependencies: Record<string, unknown>;
    query: string | undefined;
    signal: AbortSignal;
  },
): Promise<TItem[]> {
  const merged: TItem[] = [];
  let cursor: string | undefined;
  // A cursor that never advances would loop forever; the seen-set breaks a
  // buggy adapter that keeps returning the same cursor. The loop always exits
  // via a break (no more cursor, or a repeated one), so the condition is `true`.
  const seen = new Set<string>();
  for (;;) {
    input.signal.throwIfAborted();
    const page: { items: TItem[]; nextCursor?: string } =
      await source.resolvePage({
        cursor,
        dependencies: input.dependencies,
        query: input.query,
        signal: input.signal,
      });
    merged.push(...page.items);
    const next = page.nextCursor;
    if (next === undefined || seen.has(next)) break;
    seen.add(next);
    cursor = next;
  }
  return merged;
}

/**
 * Resolves ONLY the first page of a paginated source and reports whether more
 * pages exist (`hasMore`). Used by the flat picker so an open/browse shows the
 * first `$top` rows plus a "more results" hint instead of draining the whole
 * directory. The hierarchical picker still uses {@link resolveAllPages} to load a
 * full tree level. Exported so the single-page behaviour is testable without a
 * DOM.
 */
export async function resolveFirstPage<TItem, TValue>(
  source: OptionSource<TItem, TValue> & {
    resolvePage(input: {
      cursor?: string;
      dependencies: Record<string, unknown>;
      query?: string;
      signal: AbortSignal;
    }): Promise<{ items: TItem[]; nextCursor?: string }>;
  },
  input: {
    dependencies: Record<string, unknown>;
    query: string | undefined;
    signal: AbortSignal;
  },
): Promise<{ hasMore: boolean; items: TItem[] }> {
  input.signal.throwIfAborted();
  const page = await source.resolvePage({
    dependencies: input.dependencies,
    query: input.query,
    signal: input.signal,
  });
  return { hasMore: page.nextCursor !== undefined, items: page.items };
}

/**
 * The generic, datasource-agnostic picker. It wraps `source.resolve` (or, for a
 * paginated source, a page-by-page drain of `resolvePage`+`nextCursor`) as an
 * AsyncOp, drives it through `useAsyncResolver` (which owns loading, error,
 * retry, cancellation, cascade-gating and stale-suppression), and renders the
 * result in the ns-theme Combobox. Capability detection (by presence, side-effect-free)
 * steers the behaviour: a server-filtered source turns off client-side filtering
 * and folds the query into the fetch context; a paginated source loads page by
 * page; a creatable source turns a typed value outside the list into an item; an
 * enrichable source enriches the chosen item before it reaches the form.
 *
 * The picker introduces no cloud-provider knowledge: it renders equally against
 * an in-memory mock and a remote transport. UI composes @backstage/ui and the
 * ns-theme Combobox; picker-toolbox owns no presentational UI of its own.
 */
export function ChoiceField<TItem, TValue, TConfig>(
  props: ChoiceFieldProps<TItem, TValue, TConfig>,
): React.ReactElement {
  const {
    config,
    formData,
    label,
    onChange,
    openOnFocus = true,
    source,
    validKeys,
    value,
  } = props;

  // Capability detection is pure and side-effect-free: it inspects descriptors,
  // never calls a resolve path. Memoised on the source identity.
  const capabilities = useMemo(() => detectCapabilities(source), [source]);
  const serverFiltered = capabilities.serverFiltered;
  const paginated = capabilities.paginated;
  const queryRequired = capabilities.queryRequired;

  // The client-side search query. For a server-filtered source it is part of the
  // fetch context; otherwise it only narrows the already-fetched list.
  const [query, setQuery] = useState('');
  // The pending enrichment error, surfaced to the user (BLOCKER 4: enrichment
  // failure must not be silently swallowed).
  const [enrichError, setEnrichError] = useState<string>();
  // The in-flight enrichment controller, per picker instance. A new selection
  // aborts the previous enrichment so a stale enriched answer cannot overwrite a
  // newer selection.
  const enrichControllerRef = useRef<AbortController | undefined>(undefined);

  // On unmount, abort any enrichment still in flight so its late resolution never
  // calls `onChange`/`setEnrichError` on an unmounted picker (no leak, no
  // state-update-after-unmount).
  useEffect(
    () => () => {
      enrichControllerRef.current?.abort();
    },
    [],
  );

  const cascade = resolveCascadeState(config, validKeys, formData);
  const dependencies = cascade.kind === 'ready' ? cascade.dependencies : {};

  // The fetch context. It is NOT memoised: `useAsyncResolver` reads `ctx` through
  // a ref and gates its effect on the structural key derived from `cacheKey(ctx)`
  // (a `JSON.stringify` over the dependencies + query below), not on the object
  // identity of `ctx`. A fresh object per render therefore causes no re-fetch, and
  // there is no exhaustive-deps concern because there is no memo to key.
  const ctx: ResolveContext = {
    dependencies,
    paginated,
    query,
    queryRequired,
    serverFiltered,
  };

  // The AsyncOp: wrap the adapter resolve behind the { dependencies, query, signal }
  // signature the contract guarantees. A paginated source is drained page by page
  // via resolvePage+nextCursor; a flat source uses the single resolve. For a
  // client-filtered source the query is omitted from the fetch (the full list is
  // fetched once and filtered locally).
  const op = useCallback(
    async (
      resolveCtx: ResolveContext,
      signal: AbortSignal,
    ): Promise<{ hasMore: boolean; items: readonly TItem[] }> => {
      const fetchQuery = resolveCtx.serverFiltered
        ? resolveCtx.query
        : undefined;
      if (resolveCtx.paginated && isPaginatedOptionSource(source)) {
        // The flat picker shows only the first page and signals more via
        // `hasMore`, so a browse never drains the whole directory.
        return resolveFirstPage(source, {
          dependencies: resolveCtx.dependencies,
          query: fetchQuery,
          signal,
        });
      }
      const items = await source.resolve({
        dependencies: resolveCtx.dependencies,
        query: fetchQuery,
        signal,
      });
      return { hasMore: false, items };
    },
    [source],
  );

  // Readiness derives from BOTH the cascade AND the required-query policy: a
  // required source with a blank (or whitespace-only) query is not ready, so
  // `useAsyncResolver`'s existing cascade-gating (invariant 1) keeps it idle and
  // departs zero calls — the same gate that already protects an unfilled parent.
  const isReady = useCallback(
    (resolveCtx: ResolveContext) =>
      cascade.kind === 'ready' &&
      (!resolveCtx.queryRequired || resolveCtx.query.trim().length > 0),
    [cascade.kind],
  );

  // A stable structural cache key over the dependencies and (when server-filtered)
  // the query, so the non-serialisable-context dev fallback in useAsyncResolver
  // never fires and equal contexts share a cache entry.
  const cacheKey = useCallback(
    (resolveCtx: ResolveContext) =>
      JSON.stringify([
        resolveCtx.dependencies,
        resolveCtx.serverFiltered ? resolveCtx.query : null,
      ]),
    [],
  );

  // Whether the CURRENT query may be debounced: a required query that is blank
  // must skip the debounce entirely, so `useAsyncResolver`'s no-debounce path
  // runs `run()` synchronously in the effect and its existing not-ready branch
  // (via `isReady` above) aborts any in-flight call and goes idle immediately —
  // no 200ms wait before the stale option disappears.
  const queryReady = !queryRequired || query.trim().length > 0;

  const resolverOptions = useMemo(
    () => ({
      cacheKey,
      isReady,
      // A server-filtered source fetches per keystroke: debounce so a burst
      // becomes one call. A client-filtered source does not re-fetch on the
      // query, so no debounce. A required query that is currently blank also
      // skips the debounce, so the not-ready path is immediate.
      ...(serverFiltered && queryReady ? { debounceMs: 200 } : {}),
    }),
    [cacheKey, isReady, serverFiltered, queryReady],
  );

  const state = useAsyncResolver<
    ResolveContext,
    { hasMore: boolean; items: readonly TItem[] }
  >(op, ctx, resolverOptions);

  // Wrapped in its own memo so a non-success render yields a stable `[]` rather
  // than a fresh array each render, keeping the downstream memos/callbacks stable.
  const items = useMemo<readonly TItem[]>(
    () => (state.status === 'success' ? state.data.items : []),
    [state],
  );
  // Whether the source reported more rows beyond the first page, so the picker
  // can hint that typing narrows the results.
  const hasMore = state.status === 'success' && state.data.hasMore;
  // The resolved options carry the FULL value object, so the selection handler
  // can write the whole object (not just the id) to the form.
  const resolvedOptions = useMemo(
    () => toResolvedOptions(source, items),
    [source, items],
  );

  // Client-side filtering, unless the source filters server-side (then the list
  // already reflects the query and re-filtering would double-filter).
  const visibleOptions = useMemo<ResolvedOption<TItem, TValue>[]>(() => {
    if (serverFiltered || query.length === 0) return resolvedOptions;
    const needle = query.trim().toLowerCase();
    return resolvedOptions.filter(option =>
      option.label.toLowerCase().includes(needle),
    );
  }, [resolvedOptions, query, serverFiltered]);

  const options = useMemo(
    () =>
      visibleOptions.map(({ id, label: optionLabel }) => ({
        label: optionLabel,
        value: id,
      })),
    [visibleOptions],
  );

  // The currently selected id (the widget key), derived from the value object.
  const selectedKey = optionValueId(value);

  // Hydrate the input text from a bound value's label. Because `inputValue` is
  // controlled, react-aria disables its own async-hydration effect (it only runs
  // when `inputValue` is uncontrolled), so a value bound from cross-step formData
  // whose option is not in the loaded page would render a blank field. The value
  // object already carries the label (`{ id, name }`), so seed `query` from it.
  // Keyed on the bound id via a ref so it fires ONLY when the id changes (a fresh
  // binding), never on every render and never while the user is typing (typing
  // moves `query`, not `selectedKey`), so it cannot fight the user's input.
  const hydratedKeyRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (selectedKey === undefined || selectedKey === hydratedKeyRef.current) {
      return;
    }
    hydratedKeyRef.current = selectedKey;
    setQuery(optionValueName(value) ?? selectedKey);
  }, [selectedKey, value]);

  const handleSelectionChange = useCallback(
    (key: string | null) => {
      setEnrichError(undefined);
      // Any newer selection cancels an enrichment still in flight, so a stale
      // enriched answer can never overwrite the newer choice.
      enrichControllerRef.current?.abort();

      // The input text is controlled via `inputValue={query}`, so react-aria will
      // NOT put the chosen label in the field on its own: without this the label
      // shows only on the next open. Drive `query` to the selected option's label
      // so the field reflects the choice immediately. A null key clears the field;
      // a key that matches no option is a creatable typed value, whose text the
      // user already typed, so `query` is left as-is. The hydration ref is synced
      // to the chosen key so the value->query effect does not re-seed this same
      // selection a second time (the effect owns cross-step binding, this owns the
      // live user selection).
      const selected = resolvedOptions.find(option => option.id === key);
      if (key === null) {
        hydratedKeyRef.current = undefined;
        setQuery('');
      } else if (selected !== undefined) {
        hydratedKeyRef.current = selected.id;
        setQuery(selected.label);
      }

      // The pure decision: what does this key mean (clear / immediate value /
      // enrich)? The component then applies the effect.
      const outcome = decideSelection(source, resolvedOptions, key);
      if (outcome.kind === 'cleared') {
        const cleared: TValue | undefined = undefined;
        onChange(cleared);
        return;
      }
      if (outcome.kind === 'immediate') {
        onChange(outcome.value);
        return;
      }

      // Enrichment (BLOCKER 4): write the unenriched object first (so the field is
      // never empty mid-flight), then overwrite with the ENRICHED object when it
      // lands. The failure is surfaced (never swallowed) and the call is
      // cancellable, so a superseded enrichment cannot win.
      onChange(outcome.provisional);
      if (!isEnrichableOptionSource(source)) return;
      const controller = new AbortController();
      enrichControllerRef.current = controller;
      const enrichable = source;
      void enrichable
        .resolveSelection(outcome.item, { signal: controller.signal })
        .then(enrichedItem => {
          if (controller.signal.aborted) return;
          onChange(enrichable.getOptionValue(enrichedItem));
        })
        .catch((error_: unknown) => {
          if (controller.signal.aborted) return;
          const message =
            error_ instanceof Error ? error_.message : String(error_);
          setEnrichError(`Enrichment failed: ${message}`);
        });
    },
    [onChange, source, resolvedOptions],
  );

  const creatable = capabilities.creatable;

  // Committing a typed, non-existent value (BLOCKER 3). react-aria does NOT fire
  // `onSelectionChange` for a custom value (it only updates the input), so a
  // creatable source commits the typed text on Enter: if the query matches no
  // visible option, route it through `handleSelectionChange` as a created value.
  // A query that exactly matches a visible option's id, or its label except
  // for case and edge spaces (trimmed, case-insensitively), is selected
  // directly: nothing else commits an exact match typed without ArrowDown, so
  // letting it fall through would select nothing, in either presentation.
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!creatable || event.key !== 'Enter') return;
      // An option under virtual focus is react-aria's to select; committing the
      // typed text as well would record two values for one Enter.
      if (event.currentTarget.getAttribute('aria-activedescendant')) return;
      const typed = query.trim();
      if (typed.length === 0) return;
      const typedLower = typed.toLowerCase();
      const match = visibleOptions.find(
        option =>
          option.id === typed || option.label.toLowerCase() === typedLower,
      );
      if (match !== undefined) {
        handleSelectionChange(match.id);
        return;
      }
      handleSelectionChange(typed);
    },
    [creatable, query, visibleOptions, handleSelectionChange],
  );

  const errorState = describeError(state, cascade, enrichError);
  // Two distinct hints. The cascade hint ("Choose X first") belongs BELOW the
  // field: it is a form instruction shown while the picker is not yet fetchable.
  // The "more results" hint belongs INSIDE the dropdown as a footer (Combobox
  // renders it outside the filtered collection), so it stays visible whether the
  // list was opened blank or narrowed by a query.
  const cascadeHint =
    cascade.kind === 'awaiting-parents'
      ? `Choose ${cascade.missing.join(', ')} first`
      : undefined;
  // `hasMore` describes the SERVER list while the count describes what is
  // VISIBLE after the query filter, so the two disagree once a term narrows the
  // page. With nothing left to show, "Showing the first 0" is nonsense and the
  // empty-state already says there are no results.
  const moreResultsHint =
    hasMore && visibleOptions.length > 0
      ? `Showing the first ${visibleOptions.length} — type to narrow`
      : undefined;
  // The retry handler, captured only when the resolve is in an error state, so
  // the JSX renders the retry action without re-narrowing the state union.
  const retry = state.status === 'error' ? state.retry : undefined;

  // The error and the cascade instruction both go into the picker's own helper-text
  // slot, so they render under the field with the input contract shared by the
  // text-field and outlined-input overrides (4px gap, 14px, red together with the
  // label when invalid). An unknown-ref template bug therefore stays visible to
  // developers instead of producing a silent empty picker. A failed resolve keeps its
  // explicit retry action beside the field, since retrying is picker behaviour and
  // not part of the input contract.
  return (
    <Flex direction="column" gap="2">
      <Combobox
        allowsCustomValue={isCreatableOptionSource(source)}
        description={errorState ?? cascadeHint}
        hint={moreResultsHint}
        inputValue={query}
        isDisabled={cascade.kind !== 'ready'}
        isInvalid={errorState !== undefined}
        isLoading={state.status === 'loading'}
        isRequired={props.required}
        label={label}
        onInputChange={setQuery}
        onKeyDown={handleKeyDown}
        onSelectionChange={handleSelectionChange}
        openOnFocus={openOnFocus}
        options={options}
        selectedKey={selectedKey ?? null}
      />
      {retry !== undefined && (
        <Flex align="center" gap="2">
          <Button onClick={retry} size="small" variant="secondary">
            Retry
          </Button>
        </Flex>
      )}
    </Flex>
  );
}

/**
 * Builds the error message shown on the field, distinguishing a template-author
 * bug (invalid ref) from a runtime resolve failure and an enrichment failure. An
 * empty result ([]) is NOT an error: it is a valid empty list, distinct from a
 * rejected resolve.
 */
function describeError<TItem, TConfig>(
  state: AsyncResolverState<{ hasMore: boolean; items: readonly TItem[] }>,
  cascade: ReturnType<typeof resolveCascadeState<TConfig>>,
  enrichError: string | undefined,
): string | undefined {
  if (cascade.kind === 'invalid-ref') {
    return `Picker refers to unknown field '${cascade.unknownKey}'`;
  }
  if (state.status === 'error') {
    return state.error.message;
  }
  if (enrichError !== undefined) {
    return enrichError;
  }
  return undefined;
}
